import {
    ArticleInstancePlacementSourceType,
    SolvePanelPlacementModelGapPositionPreferences,
    SolverObjectiveRankingModelOrderedCriteriaItem,
    type ArticleGroupPlacement,
    type ArticleGroupPlacementLifeCycleCode,
    type SolverCostProfile,
    type SolverCostWeights,
    type MaterialListItemModel,
    type PanelGroupToUseModel,
    type PartPlacement,
    type Point3D,
    type SolvePanelPlacementModel,
    type SolverObjectiveRankingModel
} from 'api/models';
import {
    buildGroupSavedPrefabId,
    convertToDegree,
    findPoiGuidsByInstanceId,
    mapArticleInstanceToPlacement
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { EMPTY_GUID } from 'shared/constants';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export interface BuildSolvePanelPlacementRequestOptions {
    state: PlacementState;
    sessionId: string;
    systemId: string;
    poiIndex: number;
    unsavedGroupsToUse?: PanelGroupToUseModel[];
    savedGroupPrefabIdsToUse?: string[];
    materialList?: MaterialListItemModel[];
    requireVerticalGap?: boolean;
    gapPositionPreferences?: SolvePanelPlacementModelGapPositionPreferences;
    objectiveRanking?: SolverObjectiveRankingModel;
}

function buildSolverCostProfile(weights: SolverCostWeights): SolverCostProfile {
    return {
        profileId: undefined as unknown as string,
        version: undefined as unknown as string,
        weights
    };
}

function getAnchorPart(parts: PartPlacement[]): PartPlacement | null {
    return parts.length > 0 ? parts[0] : null;
}

function subtractPoints(left: Point3D, right: Point3D): Point3D {
    return {
        x: left.x - right.x,
        y: left.y - right.y,
        z: left.z - right.z
    };
}

function dot(left: Point3D, right: Point3D): number {
    return left.x * right.x + left.y * right.y + left.z * right.z;
}

function normalize(point: Point3D): Point3D {
    const length = Math.sqrt(point.x * point.x + point.y * point.y + point.z * point.z);
    if (length === 0) {
        return { x: 1, y: 0, z: 0 };
    }

    return {
        x: point.x / length,
        y: point.y / length,
        z: point.z / length
    };
}

function cross(left: Point3D, right: Point3D): Point3D {
    return {
        x: left.y * right.z - left.z * right.y,
        y: left.z * right.x - left.x * right.z,
        z: left.x * right.y - left.y * right.x
    };
}

function resolveGroupDirection(alignment: Point3D, fallbackDirection: Point3D): Point3D {
    const wallUp = { x: 0, y: 0, z: 1 };
    const direction = cross(alignment, wallUp);
    const length = Math.sqrt(dot(direction, direction));

    return length > 0.0001 ? normalize(direction) : fallbackDirection;
}

function resolveWallDirection(state: PlacementState, poiIndex: number): Point3D {
    const vertices = state.pois[poiIndex]?.poi.vertices ?? [];
    if (vertices.length < 2) {
        return { x: 1, y: 0, z: 0 };
    }

    const minZ = Math.min(...vertices.map((vertex) => vertex.z));
    const bottomVertices = vertices.filter((vertex) => Math.abs(vertex.z - minZ) < 0.0001);
    const candidates = bottomVertices.length >= 2 ? bottomVertices : vertices;

    let start = candidates[0];
    let end = candidates[1] ?? candidates[0];
    let maxDistance = -1;

    for (let i = 0; i < candidates.length; i += 1) {
        for (let j = i + 1; j < candidates.length; j += 1) {
            const delta = subtractPoints(candidates[j], candidates[i]);
            const distance = dot(delta, delta);
            if (distance > maxDistance) {
                maxDistance = distance;
                start = candidates[i];
                end = candidates[j];
            }
        }
    }

    return normalize(subtractPoints(end, start));
}

function buildUnsavedGroupsToUse(
    state: PlacementState,
    selectedPoiGuid: string,
    poiIndex: number
): PanelGroupToUseModel[] {
    const groupsToUse: PanelGroupToUseModel[] = [];
    const fallbackWallDirection = resolveWallDirection(state, poiIndex);
    const wallUp = { x: 0, y: 0, z: 1 };

    Object.values(state.articleGroups).forEach((group) => {
        const memberPlacements: Array<{
            articleId: string;
            articleNumber: string;
            parts: PartPlacement[];
        }> = [];
        let isGroupOnSelectedPoi = false;

        group.members.forEach((member) => {
            const article = state.articles[member.articleId];
            const instance = article?.articleInstances[member.instanceId];
            if (!article || !instance) {
                return;
            }

            const memberPoiGuids = findPoiGuidsByInstanceId(state, member.instanceId);
            if (memberPoiGuids.includes(selectedPoiGuid)) {
                isGroupOnSelectedPoi = true;
            }

            memberPlacements.push({
                articleId: member.articleId,
                articleNumber: article.articleNumber,
                parts: mapArticleInstanceToPlacement(member.instanceId, instance, memberPoiGuids, false).parts
            });
        });

        if (isGroupOnSelectedPoi || memberPlacements.length === 0) {
            return;
        }

        const anchorPart = getAnchorPart(memberPlacements[0].parts);
        if (!anchorPart) {
            return;
        }
        const groupDirection = resolveGroupDirection(anchorPart.alignment, fallbackWallDirection);

        groupsToUse.push({
            groupId: group.id,
            name: group.name,
            position: { ...anchorPart.position },
            alignment: { ...anchorPart.alignment },
            scale: { ...anchorPart.scale },
            rotation: convertToDegree(anchorPart.rotation),
            components: memberPlacements
                .map((memberPlacement) => {
                    const memberAnchorPart = getAnchorPart(memberPlacement.parts);
                    if (!memberAnchorPart) {
                        return null;
                    }

                    return {
                        articleGuid: memberPlacement.articleId,
                        articleNumber: memberPlacement.articleNumber,
                        representationGuid: EMPTY_GUID,
                        name: memberAnchorPart.name,
                        relativePosition: {
                            x: dot(subtractPoints(memberAnchorPart.position, anchorPart.position), groupDirection),
                            y: dot(subtractPoints(memberAnchorPart.position, anchorPart.position), wallUp),
                            z: 0
                        },
                        alignment: { ...memberAnchorPart.alignment },
                        scale: { ...memberAnchorPart.scale },
                        rotation: convertToDegree(memberAnchorPart.rotation)
                    };
                })
                .filter((component) => component !== null)
        });
    });

    return groupsToUse;
}

function buildExistingPlacements(state: PlacementState, poiIndex: number): ArticleGroupPlacement[] {
    const poiData = state.pois[poiIndex];
    if (!poiData) {
        return [];
    }

    const placements: ArticleGroupPlacement[] = [];
    const poiGuid = poiData.poi.poiGuid;

    Object.entries(poiData.articles).forEach(([articleId, instanceIds]) => {
        const article = state.articles[articleId];
        if (!article || instanceIds.length === 0) {
            return;
        }

        const instances = instanceIds
            .map((instanceId) => {
                const articleInstance = article.articleInstances[instanceId];
                if (!articleInstance) {
                    return null;
                }

                const groupId = state.instanceToGroupMap[instanceId] ?? null;
                const groupName = groupId ? (state.articleGroups[groupId]?.name ?? null) : null;
                const sourceSavedGroupPrefabId = groupId
                    ? (buildGroupSavedPrefabId(state, groupId) ?? articleInstance.sourceSavedGroupPrefabId ?? null)
                    : (articleInstance.sourceSavedGroupPrefabId ?? null);

                let sourceType: ArticleInstancePlacementSourceType | null = null;
                if (groupId) {
                    sourceType = sourceSavedGroupPrefabId
                        ? ArticleInstancePlacementSourceType.SavedGroup
                        : ArticleInstancePlacementSourceType.UnsavedGroup;
                }

                const placement = mapArticleInstanceToPlacement(
                    instanceId,
                    articleInstance,
                    articleInstance.poiGuids ?? [poiGuid],
                    false
                );

                return {
                    ...placement,
                    parts: placement.parts.map((part) => ({ ...part, rotation: convertToDegree(part.rotation) })),
                    // Solver-only constraint: treat current placements as fixed while solving.
                    isLocked: true,
                    sourceType,
                    sourceGroupId: groupId ?? articleInstance.sourceGroupId ?? null,
                    sourceGroupName: groupName ?? articleInstance.sourceGroupName ?? null,
                    sourceGroupInstanceId: articleInstance.sourceGroupInstanceId ?? null,
                    sourceSavedGroupPrefabId
                };
            })
            .filter((instance) => instance !== null);

        if (instances.length === 0) {
            return;
        }

        placements.push({
            articleGuid: articleId,
            articleNumber: article.articleNumber,
            lifeCycleCode: article.lifeCycleCode as ArticleGroupPlacementLifeCycleCode,
            representationGuid: EMPTY_GUID,
            instances
        });
    });

    return placements;
}

export function buildSolvePanelPlacementRequest(
    options: BuildSolvePanelPlacementRequestOptions
): SolvePanelPlacementModel {
    const {
        state,
        sessionId,
        systemId,
        poiIndex,
        unsavedGroupsToUse,
        savedGroupPrefabIdsToUse,
        materialList,
        requireVerticalGap,
        gapPositionPreferences,
        objectiveRanking
    } = options;

    const poiId = state.pois[poiIndex]?.poi.poiGuid ?? EMPTY_GUID;
    const derivedUnsavedGroupsToUse = buildUnsavedGroupsToUse(state, poiId, poiIndex);
    const automatedPanelSettings = state.automatedPlacementSettingsByType.Panels;
    const resolvedWeights =
        requireVerticalGap === undefined && gapPositionPreferences === undefined
            ? automatedPanelSettings.solverCostWeights
            : state.solverCostWeights;

    return {
        sessionId,
        poiId,
        systemId,
        existingPlacements: buildExistingPlacements(state, poiIndex),
        unsavedGroupsToUse: unsavedGroupsToUse ?? derivedUnsavedGroupsToUse,
        savedGroupPrefabIdsToUse: savedGroupPrefabIdsToUse ?? [],
        materialList: materialList ?? [],
        requireVerticalGap: requireVerticalGap ?? automatedPanelSettings.requireVerticalGap,
        gapPositionPreferences: gapPositionPreferences ?? automatedPanelSettings.gapPositionPreferences,
        objectiveRanking: objectiveRanking ?? {
            orderedCriteria: [SolverObjectiveRankingModelOrderedCriteriaItem.MaximizePlacedArea]
        },
        costProfile: buildSolverCostProfile(resolvedWeights)
    };
}
